<script setup lang="ts">
import { wgpImage } from '@/data/landing-v3-assets';
import { mockSlideHtml, mockSlideText } from '@/lib/mock-slide-i18n';
type Wgp9SelectableElement =
  | 'wgp9-rectangle'
  | 'wgp9-shape-1'
  | 'wgp9-headline'
  | 'wgp9-body'
  | 'wgp9-paid-social';

defineProps<{
  selectedElement?: string | null;
}>();

const emit = defineEmits<{
  select: [elementId: Wgp9SelectableElement, event: MouseEvent];
}>();

const wgp9ImageUrl = `url(${wgpImage('slide09-next-update-01.png')})`;
const wgp9ShapeUrl = `url(${wgpImage('slide09-next-update-02.png')})`;
const wgp9DividerUrl = `url(${wgpImage('slide09-next-update-03.svg')})`;
</script>

<template>
  <div class="wgp9-artboard absolute inset-0">
    <div class="wgp9-stage">
      <div class="wgp9-flex-column-fc">
        <div
          class="wgp9-rectangle wgp9-selectable"
          :class="{ 'is-selected': selectedElement === 'wgp9-rectangle' }"
          @click="emit('select', 'wgp9-rectangle', $event)"
        />
        <div
          class="wgp9-shape-1 wgp9-selectable"
          :class="{ 'is-selected': selectedElement === 'wgp9-shape-1' }"
          @click="emit('select', 'wgp9-shape-1', $event)"
        />
      </div>
      <div class="wgp9-flex-column">
        <span
          class="wgp9-headline wgp9-selectable"
          :class="{ 'is-selected': selectedElement === 'wgp9-headline' }"
          @click="emit('select', 'wgp9-headline', $event)"
        >
          {{ mockSlideText('wgp.09.tag', 'Next update: clearer conversion, lower CAC.') }}
        </span>
        <div class="wgp9-divider" />
        <span
          class="wgp9-body wgp9-selectable"
          :class="{ 'is-selected': selectedElement === 'wgp9-body' }"
          @click="emit('select', 'wgp9-body', $event)"
          v-html="
            mockSlideHtml(
              'wgp.09.headline',
              'Bloom&amp;Co Weekly Pulse · Nov 11–17<br />Data: Shopify + GA4 + Meta Ads<br />Owner: Mia (Growth &amp; Ops)'
            )
          "
        ></span>
        <span
          class="wgp9-paid-social-efficiency wgp9-selectable"
          :class="{ 'is-selected': selectedElement === 'wgp9-paid-social' }"
          @click="emit('select', 'wgp9-paid-social', $event)"
        >
          {{
            mockSlideText(
              'wgp.09.url',
              'Primary focus: Paid Social efficiency + Checkout clarity + Email automations'
            )
          }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wgp9-artboard {
  overflow: hidden;
  background: linear-gradient(180deg, #f57a59, #f3e7db);
  font-size: 0;
}

.wgp9-artboard,
.wgp9-artboard * {
  box-sizing: border-box;
}

.wgp9-stage {
  position: relative;
  width: 1920px;
  height: 1080px;
  transform: translate(0, 0) scale(0.1875);
  transform-origin: top left;
  font-size: 0;
}

.wgp9-selectable {
  cursor: pointer;
}

.wgp9-selectable.is-selected {
  outline: 8px solid #fe713b;
  outline-offset: 0;
  z-index: 30;
}

.wgp9-flex-column-fc {
  position: absolute;
  top: -27.805px;
  right: -121.094px;
  bottom: 0;
  left: 1525px;
  z-index: 3;
}

.wgp9-rectangle {
  position: absolute;
  width: 476.094px;
  height: 595.609px;
  top: 0;
  left: 40px;
  background: v-bind(wgp9ImageUrl) no-repeat center;
  background-size: cover;
  z-index: 2;
}

.wgp9-shape-1 {
  position: absolute;
  width: 353.164px;
  height: 540px;
  right: 121.094px;
  bottom: 0;
  background: v-bind(wgp9ShapeUrl) no-repeat center;
  background-size: cover;
}

.wgp9-flex-column {
  position: absolute;
  top: 51.746px;
  right: 435px;
  bottom: 40px;
  left: 40px;
  font-size: 0;
  z-index: 7;
}

.wgp9-headline {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  width: 1445px;
  height: 570px;
  color: #000000;
  font-family: 'Fraunces', 'Source Serif 4', serif;
  font-size: 200px;
  font-weight: 400;
  line-height: 190px;
  text-align: left;
  letter-spacing: -8px;
  z-index: 7;
}

.wgp9-divider {
  position: relative;
  width: 1445px;
  height: 1.5px;
  margin: 280.754px 0 0 0;
  background: v-bind(wgp9DividerUrl) no-repeat center;
  background-size: cover;
  z-index: 4;
}

.wgp9-body {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  position: absolute;
  width: 700px;
  height: 102px;
  bottom: 0;
  left: 0;
  color: #000000;
  font-family: 'Source Serif 4', serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 33.6px;
  text-align: left;
  letter-spacing: -0.96px;
  z-index: 6;
  overflow: hidden;
}

.wgp9-paid-social-efficiency {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  position: absolute;
  width: 700px;
  height: 68px;
  right: 0;
  bottom: 0;
  color: #000000;
  font-family: 'Source Serif 4', serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 33.6px;
  text-align: right;
  letter-spacing: -0.96px;
  z-index: 5;
}
</style>
